import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal, TextArea } from '@/components_deprecated'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import { DetectBlur } from '@/containers/util-containers'
import { Field } from '@/modules/companies'
import styled, { css } from 'styled-components'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ButtonFlexContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
`

const PartOfDayButton = styled.div<{ selected: boolean }>`
  ${props =>
    props.selected &&
    css`
      color: white;
      background: ${props.theme.colors.primary};

      &:first-child {
        background: gray;
      }
      &:hover {
        cursor: default;
      }
    `}
  border: 1px solid ${props => props.theme.colors.gray6};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  padding: 1rem;
  cursor: pointer;
  min-width: 50px;
  text-align: center;
`

const BUTTON_PROPS = [
  { value: 0, text: '0' },
  { value: 0.25, text: '1/4' },
  { value: 0.5, text: '1/2' },
  { value: 0.75, text: '3/4' },
  { value: 1, text: '1' },
]

interface PartOfDayButtonProps {
  value: number
  text: string
  selected: boolean
  onClick: (value: number, event?: MouseEvent | undefined) => void
}

const PartOfDay: React.FC<PartOfDayButtonProps> = ({
  value,
  text,
  selected,
  onClick,
}) => {
  return (
    <PartOfDayButton selected={selected} onClick={() => onClick(value)}>
      {text}
    </PartOfDayButton>
  )
}
export interface DriverDietForm {
  userId: string
  salaryCodeId: string | null
  partOfDay: number
  comment: string
}

interface DriverDietPromptProps {
  initialData: DriverDietForm
  edit: boolean
  simpleView?: boolean
  commentMandatory?: boolean
  onSubmit: PromptResolve<DriverDietForm | null>
}

export const DriverDietPrompt: React.FC<DriverDietPromptProps> = ({
  initialData,
  edit,
  simpleView,
  commentMandatory,
  onSubmit,
}) => {
  const translations = useTranslate({
    value: 'common.value',
    addDiet: 'activities.add-diet',
    changeDiet: 'activities.change-diet',
    salaryCode: 'settings.salary-code',
    comment: 'common.comment',
    required: 'common.required',

    update: 'common.update',
  })

  const {
    formValues: form,
    formErrors,
    formValid,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<DriverDietForm>({
    values: initialData,
    validators: {
      comment: value =>
        commentMandatory && !value ? translations.required : null,
    },
    config: {
      initAsInvalid: true,
      validateImmediately: true,
    },
  })

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={edit ? translations.changeDiet : translations.addDiet}
        submitText={translations.update}
        submitDisabled={!formValid}
        width="500px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <Form>
          <Field>
            <label>{translations.salaryCode}</label>
            <SalaryCodeSelect
              fullWidth
              nullable
              value={form.salaryCodeId}
              isDiet={true}
              setOnLoad={true}
              onChange={handler('salaryCodeId')}
            />
            <ButtonFlexContainer>
              {BUTTON_PROPS.filter(props =>
                simpleView ? props.value === 1 || props.value === 0 : true
              ).map(props => (
                <PartOfDay
                  key={props.value}
                  selected={form.partOfDay === props.value}
                  {...props}
                  onClick={handler('partOfDay')}
                />
              ))}
            </ButtonFlexContainer>
          </Field>
          <Field>
            <label>{translations.comment}</label>
            <TextArea
              value={form.comment}
              error={formErrors.comment}
              onChange={handler('comment')}
            />
          </Field>
        </Form>
      </RegularModal>
    </DetectBlur>
  )
}
